import Link from 'next/link';
import { FC, MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AdTypeEnum } from '@/__generated_graphql__/graphql';
import { getStatus, isValidChipValue } from '@/components/Card/Card.helpers';
import { CardProps, WrapperProps } from '@/components/Card/Card.types';
import { CardMenu } from '@/components/Card/CardMenu';
import { ProgressBar } from '@/components/Card/ProgressBar';
import { Avatar } from '@/components/ui/Avatar/Avatar';
import { CardChip } from '@/components/ui/CardChip/CardChip';
import {
  BookmarkIcon,
  BumpIcon,
  CrownIcon,
  LightningIcon,
  VerifiedIcon
} from '@/components/ui/Icons/Icons';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { AD_TYPE, CardStatus } from '@/constants/common';
import { PAYMENT_MODAL_CY } from '@/constants/test-id';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useToggleBookmark } from '@/hooks/useToggleBookmark';

import * as S from './Card.styles';

const { EXPIRED, APPROVED, PENDING, AWAITING_PAYMENT } = CardStatus;

const Wrapper: FC<WrapperProps> = ({ href, onClick, children, targetType }) => {
  if (href) {
    return (
      <Link href={href} onClick={onClick} prefetch={false} target={targetType}>
        {children}
      </Link>
    );
  }
  return <>{children}</>;
};

export const Card: FC<CardProps> = props => {
  const {
    id,
    title,
    subtitle,
    updatedAt,
    createdAt,
    bumpedAt,
    location,
    education,
    experience,
    salaryFrom,
    salaryTo,
    status,
    image,
    daysLeft,
    activeTab,
    isBookmarked,
    isFeatured = false,
    isBumped = false,
    isDeactivated,
    isVerified = false,
    isMine,
    isProfile,
    isFavoriteVisible = true,
    __typename,
    className,
    link,
    isPublishPreview = false,
    isHeaderVisible = true,
    targetType,
    onPromote,
    onActivate,
    onClick
  } = props;

  const showActions = isProfile && isMine && ![PENDING].includes(status);

  const adType =
    __typename === AD_TYPE.RESUME ? AdTypeEnum.Resume : AdTypeEnum.Vacancy;
  const isResume = adType === AdTypeEnum.Resume;
  const isMobile = useMediaQuery('mobile');

  const { t } = useTranslation();

  const toggleBookmark = useToggleBookmark();

  const handleToggleBookmark = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!id) return;
    toggleBookmark(id, adType, isBookmarked);
  };

  const handlePreventLink = (e: MouseEvent<HTMLAnchorElement>) => {
    if (isDeactivated) {
      e.stopPropagation();
      e.preventDefault();
    } else if (id) {
      onClick?.(id);
    }
  };

  const tags = useMemo(
    () => [
      { value: location, key: 'location' },
      {
        value: education ? education : '',
        key: 'education'
      },
      {
        value: experience && `${t('Card.experience')} ${experience}`,
        key: 'experience'
      }
    ],
    [location, education, experience, t]
  );

  const handleActivate = (
    e: MouseEvent<HTMLLinkElement> | MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    e.preventDefault();
    onActivate?.();
  };

  const handlePromote = (
    e: MouseEvent<HTMLLinkElement> | MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    e.preventDefault();
    onPromote?.();
  };

  return (
    <Wrapper href={link} onClick={handlePreventLink} targetType={targetType}>
      <S.CardWrapper
        className={className}
        data-cy="ad-card"
        $isFeatured={!!isFeatured}
        $isMobile={isMobile}
        $isLink={!!link}
        $isDeactivated={isDeactivated}
        $isResume={isResume}
      >
        <S.Header $isHeaderVisible={isHeaderVisible}>
          <S.PublishedAtText
            data-cy="ad-card-date"
            variant={isMobile ? 'caption3' : 'caption1'}
          >
            {t('Card.intlDateTime', {
              val: isProfile ? (createdAt ?? updatedAt) : bumpedAt
            })}
          </S.PublishedAtText>
          {isProfile && isMine ? (
            <S.Status data-cy="ad-card-status" $status={status}>
              {t(getStatus(status))}
            </S.Status>
          ) : (
            <S.AddToBookmarksButton
              disabled={isDeactivated && !isBookmarked}
              data-testid="bookmarkButton"
              aria-label="Add to bookmark"
              $isBookmarked={isBookmarked}
              $isDeactivated={isDeactivated && !isBookmarked}
              $isVisible={isFavoriteVisible}
              onClick={handleToggleBookmark}
            >
              <BookmarkIcon />
            </S.AddToBookmarksButton>
          )}
        </S.Header>

        <S.Content
          $isPublishPreview={isPublishPreview}
          $isHeaderVisible={isHeaderVisible}
        >
          <S.InfoBlock $isDeactivated={isDeactivated}>
            <S.MainInfo>
              <S.TitleRow>
                <S.Title data-cy="ad-card-title" variant="caption2">
                  {title}
                </S.Title>
                {isVerified && <VerifiedIcon />}
              </S.TitleRow>
              <S.Subtitle
                data-cy="ad-card-subtitle"
                variant={isMobile ? 'title4' : 'title3'}
              >
                {subtitle}
              </S.Subtitle>
            </S.MainInfo>

            <Avatar
              dataCy="ad-card-logo"
              size={isMobile ? 40 : 55}
              name={title}
              image={image}
              showInitials={adType === AdTypeEnum.Vacancy}
            />
          </S.InfoBlock>

          {showActions ? (
            <S.Actions $extraPadding={isBumped || status === EXPIRED}>
              {status === APPROVED && (
                <S.PromoteWrapper>
                  {!isBumped && <ProgressBar daysLeft={daysLeft} />}

                  <S.PromoteButton
                    startIcon={<LightningIcon />}
                    onClick={handlePromote}
                    data-cy={PAYMENT_MODAL_CY.OPEN_PROMOTE_BTN}
                  >
                    {t('Card.advertise_label')}
                  </S.PromoteButton>
                </S.PromoteWrapper>
              )}

              {status === AWAITING_PAYMENT && (
                <S.PayWrapper>
                  <S.PayButton
                    size="s"
                    variant="accent"
                    onClick={handleActivate}
                  >
                    {t('Card.complete_label')}
                  </S.PayButton>
                </S.PayWrapper>
              )}
              {/* TODO: Implement activate button */}
              {/* {status === EXPIRED && (
                <S.ActivateButton onClick={onActivate}>
                  {t('Card.activate_label')}
                </S.ActivateButton>
              )} */}
            </S.Actions>
          ) : (
            <S.Tags $isDeactivated={false}>
              {tags
                .filter(({ value }) => isValidChipValue(value ?? ''))
                .map(({ value, key }) => (
                  <span key={key} style={undefined}>
                    <CardChip
                      label={value as string}
                      isLabelClipped={key === 'education'}
                      dataCy={`ad-card-chip-${key}`}
                      isMobile={isMobile}
                    />
                  </span>
                ))}
            </S.Tags>
          )}
        </S.Content>
        <S.Footer>
          <S.Salary
            $isDeactivated={isDeactivated}
            data-cy="ad-card-salary"
            variant={isMobile ? 'h5' : 'h4'}
          >
            {t('Card.salary', {
              val: { salaryFrom, salaryTo },
              interpolation: { escapeValue: false }
            })}
          </S.Salary>

          <S.ChipsWrapper>
            <>
              {isDeactivated && (
                <S.DeactivatedChip>
                  <Typography variant="caption3">
                    {t('Card.deactivated')}
                  </Typography>
                </S.DeactivatedChip>
              )}

              {isFeatured && (
                <S.PremiumChip>
                  <CrownIcon />
                </S.PremiumChip>
              )}
              {isBumped && (
                <S.BumpChip>
                  <BumpIcon />
                </S.BumpChip>
              )}
            </>

            {showActions && (
              <CardMenu
                onPromote={onPromote}
                subtitle={subtitle}
                adType={adType}
                id={id}
                status={status}
                activeTab={activeTab}
              />
            )}
          </S.ChipsWrapper>
        </S.Footer>
      </S.CardWrapper>
    </Wrapper>
  );
};
